/**
 * Customers: finding a business its next customer
 * ([ADR 0034](../../../docs/adr/0034-marketing-tools-are-built-in-joogoos-and-overbill-is-their-first-customer.md),
 * [23](../../../docs/23-customers.md)).
 *
 * Ported from Overbill's Reach (its `docs/50`), which was built for one business and is now one configuration of
 * this. What changed in the port is the part that was Overbill:
 *
 * - **No partners and no territories.** The workspace is the business (ADR 0006) and its members find customers for
 *   it. A workspace has **markets** — the countries it sells to — and a member may be given some, so a company in
 *   Ireland lands with the person who works Ireland. `workspace_id` stands where `partner_id` and `territory` stood.
 * - **Nothing is hard-coded for one business.** Who we look for, the judge's scored table, the offer, the ask and
 *   the words never to write are rows of a workspace, written from its business page ([ADR 0029](../../../docs/adr/0029-one-lookup-for-every-business-and-the-owner-chooses-what-to-keep.md))
 *   and edited by its owner. Overbill's `judge.yaml` and its seven letters are imported as Overbill's rows; they are
 *   nobody's defaults.
 * - **Two kinds of customer** (the owner, 2026-09-20). Some businesses want other businesses; some want the public.
 *   The finding, the judging and the measuring are one loop for both. What may be *sent* is not, and that is what
 *   `CustomerAudience` and `CustomerRoute` carry.
 */

/**
 * Who this group is meant to win.
 *
 * `business` — a company, which in most countries may be written to unasked with an opt-out.
 * `public` — people, who nearly nowhere may be. A group of this kind never ends in a cold letter to a person; the
 * route below says what it ends in instead.
 */
export const CUSTOMER_AUDIENCES = ["business", "public"] as const;
export type CustomerAudience = (typeof CUSTOMER_AUDIENCES)[number];

/**
 * Where the letters actually go.
 *
 * `direct` — to the company we found. The only route for a `business` group.
 * `through_business` — to the businesses and places that already hold this public: the letting agent, the gym, the
 *   office manager. The prospect is a company, so the letter is lawful; what it offers is the public behind it.
 * `own_list` — to people the customer already has a relationship with, from a list they brought. Nothing is found:
 *   the finder is switched off for this route and the judge has nothing to judge.
 */
export const CUSTOMER_ROUTES = ["direct", "through_business", "own_list"] as const;
export type CustomerRoute = (typeof CUSTOMER_ROUTES)[number];

/** Which routes an audience may take. A `business` group writes to the business; a `public` group never does. */
export function routeAllowed(audience: CustomerAudience, route: CustomerRoute): boolean {
  return audience === "business" ? route === "direct" : route !== "direct";
}

/** The routes offered for an audience, in the order a screen should show them. */
export function routesFor(audience: CustomerAudience): readonly CustomerRoute[] {
  return CUSTOMER_ROUTES.filter((r) => routeAllowed(audience, r));
}

/**
 * The routes a finder can go and look for companies under.
 *
 * **Not the same question as who the letters are for**, which is the mistake this predicate exists to stop. A
 * `through_business` group's prospect *is* a company — the letting agent, the gym, the office manager — so it is
 * found, read and judged exactly as a `direct` group's is; only what the letter asks of it differs. The one route
 * with nothing to search for is `own_list`, where the customer brought the names themselves.
 *
 * Every place that used to ask `route === "direct"` asks this instead: the week's cells, the front door's list of
 * what is still missing, and the find route. They were three copies of one judgement and two of them were wrong,
 * which left a workspace whose groups are all for the public being told for ever to go and set up a group.
 */
export const FINDABLE_ROUTES = CUSTOMER_ROUTES.filter((r) => r !== "own_list");

/** Whether a search can go and find more of this group. */
export function routeFinds(route: CustomerRoute): boolean {
  return route !== "own_list";
}

/**
 * Whether this group's letters ask something other than the workspace's own ask.
 *
 * A `direct` group is written to because we want its business; the ask on Set up is that ask. A group reached
 * `through_business` is written to because it holds somebody else we want, so the same sentence would be asking a
 * gym to buy what its members want — which is why a group of that kind carries an ask of its own (23 §two kinds).
 */
export function groupAsksItsOwn(route: CustomerRoute): boolean {
  return route !== "direct";
}

/**
 * The letter a group's companies get: its own offer, ask and language where it has them, the business's where not
 * (2026-10-21).
 *
 * A group is a kind of customer in some places, so it is where a letter's middle belongs: a business that sells to
 * Dubai and to Leeds offers them different things, and switching what a letter says should be editing one group,
 * not rewriting Set up between two markets. A group with nothing of its own writes exactly the letter it wrote
 * before. The one rule that is not a fallback: a group reached through another business never borrows the
 * business's ask, because that ask is the wrong sentence for it (`groupAsksItsOwn`).
 */
export function groupLetter(
  group: { route: CustomerRoute; offer?: string; ask: string; language?: string } | undefined,
  settings: { offer: string; ask: string; languages: readonly string[] },
): { offer: string; ask: string; language: string } {
  const own = (t: string | undefined) => (t ?? "").trim();
  const ask = group && groupAsksItsOwn(group.route) ? own(group.ask) : (own(group?.ask) || settings.ask.trim());
  return {
    offer: own(group?.offer) || settings.offer.trim(),
    ask,
    language: own(group?.language) || settings.languages[0] || "en",
  };
}

/**
 * Where a group came from, and whether it is still alive (Overbill's ADR 0101, made per workspace).
 *
 * A group is a hypothesis: the loop proposes one, tries it on about ten companies and twenty letters, keeps it when
 * it answers and drops it when it cannot catch the best one in its market. `owner` groups are typed by a person;
 * `proposed` waits for their yes; `learned` was let through by the dial.
 */
export const GROUP_ORIGINS = ["owner", "proposed", "learned"] as const;
export type GroupOrigin = (typeof GROUP_ORIGINS)[number];

export const GROUP_STATES = ["trying", "kept", "dropped"] as const;
export type GroupState = (typeof GROUP_STATES)[number];

/**
 * Where a company has got to (Overbill's `docs/50` §3).
 *
 * The whole list is declared here and checked by the table, so a stage that starts using a state is not also a
 * migration widening a constraint under a running box.
 */
export const PROSPECT_STATES = [
  "found", "judged", "ready", "drafted", "approved", "sent", "followed", "replied", "meeting", "signed_in",
  "paying", "no_fit", "no_reply", "declined", "unsubscribed", "bounced", "suppressed",
  // *Not now*, which is not a *no*: keeping the two apart is the difference between a list that pesters people
  // and one that remembers them (23 §follow).
  "later",
] as const;
export type ProspectState = (typeof PROSPECT_STATES)[number];

/**
 * Where a company is once a first letter has gone to it.
 *
 * A second first letter is never drafted from any of these: the company has been written to, and what happens next is
 * a follow-up, an answer or nothing. The company panel offered *Write the letter* on a company already written to
 * until 2026-10-06, because the letter in hand was the only one it looked for.
 */
export const WRITTEN_TO: readonly ProspectState[] = [
  "sent", "followed", "replied", "meeting", "signed_in", "paying", "no_reply", "declined", "unsubscribed", "bounced",
  "suppressed", "later",
];

/**
 * Where a company may be when a person says *they replied* (23 §they replied): written to, and not answered as far as
 * Joogoos knows. *No reply* is among them, because a company closed after a silent sequence can still answer a month
 * later, and the answer is what counts.
 */
export const MAY_SAY_REPLIED: readonly ProspectState[] = ["sent", "followed", "no_reply"];

/** The judge's answer: a go, a look for a person to decide, or a no. Not knowing is `look`, never `no`. */
export const VERDICTS = ["go", "look", "no"] as const;
export type Verdict = (typeof VERDICTS)[number];

/** Who decided: the scored table, or a person overruling it with a reason kept in the history. */
export const JUDGED_BY = ["rules", "person"] as const;
export type JudgedBy = (typeof JUDGED_BY)[number];

/** A row of a workspace's judge: a fact worth points, or a fact that makes a no whatever the score. */
export const JUDGE_ROW_KINDS = ["score", "refusal"] as const;
export type JudgeRowKind = (typeof JUDGE_ROW_KINDS)[number];

/** Where a state change came from (ADR 0050 in Overbill; the same rule here). */
export const ACTOR_KINDS = ["person", "system", "model"] as const;
export type ActorKind = (typeof ACTOR_KINDS)[number];

/** Why a sequence stopped, once it has. */
export const STOP_REASONS = ["replied", "bounced", "opted_out", "by_hand", "finished"] as const;
export type StopReason = (typeof STOP_REASONS)[number];

/** Why an address will never be written to again from this workspace. */
export const SUPPRESSION_REASONS = ["replied_stop", "bounced", "complained", "by_hand"] as const;
export type SuppressionReason = (typeof SUPPRESSION_REASONS)[number];

/** A letter's life. `written` is a person saying they sent it themselves; `sent` is the tool having sent it. */
export const LETTER_STATES = ["draft", "approved", "written", "sent", "discarded"] as const;
export type LetterState = (typeof LETTER_STATES)[number];

/** A follow-up's life. `refused` is the gate saying no, with the reason in words a person can act on. */
export const FOLLOW_UP_STATES = ["scheduled", "sent", "cancelled", "refused", "failed"] as const;
export type FollowUpState = (typeof FOLLOW_UP_STATES)[number];

/** Who wrote the opening line. */
export const OPENER_BY = ["model", "person"] as const;
export type OpenerBy = (typeof OPENER_BY)[number];

/** A week's share of the budget goes to what earns, or to what has not been tried (Overbill's ADR 0101). */
export const PLAN_KINDS = ["earn", "try"] as const;
export type PlanKind = (typeof PLAN_KINDS)[number];

/** What the nightly reading may propose. A retirement always waits for a person, whatever the dial says. */
export const PROPOSAL_KINDS = ["reword", "narrow", "retire", "try_market"] as const;
export type ProposalKind = (typeof PROPOSAL_KINDS)[number];

export const PROPOSAL_STATES = ["waiting", "applied", "rejected"] as const;
export type ProposalState = (typeof PROPOSAL_STATES)[number];

/** How much of what it learns the loop may apply without being asked. A retirement is never in this. */
export const LEARNING_APPLIES = ["none", "words", "all"] as const;
export type LearningApplies = (typeof LEARNING_APPLIES)[number];

/**
 * The refusals that are in the product and cannot be switched off.
 *
 * Everything else in a judge is a row of the workspace's own table. These three are not opinions about who is a good
 * customer, they are facts about whether writing is possible or honest at all: an address nobody answers, a company
 * that has stopped trading, and a business the customer's own page names as a rival.
 */
export const GENERIC_REFUSALS = [
  { code: "free_mailbox", says: "The address is a free mailbox (gmail, outlook, yahoo)" },
  { code: "not_trading", says: "The site is parked, dead, or now something else" },
  { code: "a_rival", says: "A business named in Who else they could choose on your business page" },
] as const;
export type GenericRefusal = (typeof GENERIC_REFUSALS)[number]["code"];

/**
 * The guard, which is generic and stays in code.
 *
 * Overbill's `guard.yaml` also refused the word *freight*, which is Overbill's own rule about Overbill's own offer.
 * That kind of rule is a workspace's list of words never to write, not a line in this product.
 */
export const GUARD_RULES = [
  { code: "unbacked_claim", says: "No claim your business page does not back" },
  { code: "names_a_rival", says: "Nobody from Who else they could choose, or a hint at them" },
  { code: "unevidenced_number", says: "No percentage or number we did not read about that company" },
  { code: "fake_reply", says: "No subject dressed up as a reply" },
  { code: "no_stop_line", says: "The stop line present" },
  { code: "forbidden_word", says: "None of the words this workspace never writes" },
  { code: "unfilled_placeholder", says: "Nothing left in braces that was meant to be filled in" },
  { code: "too_long", says: "Short enough that a stranger reads it to the end" },
] as const;
export type GuardRule = (typeof GUARD_RULES)[number]["code"];

/**
 * How long a letter to a stranger may be, counted in the body alone: the greeting, the opening line, the offer,
 * the ask and the signature, without the legal footer nobody may edit.
 *
 * It is a refusal rather than a hint because the screen that would carry the hint is the one screen a person is
 * trying to leave. Two hundred and twenty words is roughly twice a good cold letter and half a brochure, so it
 * catches the offer somebody pasted out of their website without ever arguing with a letter written for the job.
 */
export const LETTER_MAX_WORDS = 220;

/** Where the judge's thresholds start for a new workspace: a go at 60, a look at 30 (Overbill's `judge.yaml`). */
export const JUDGE_GO_AT = 60;
export const JUDGE_LOOK_AT = 30;

/** The verdict a score earns, before any refusal is applied. A refusal makes a `no` whatever this says. */
export function verdictFor(score: number, goAt: number = JUDGE_GO_AT, lookAt: number = JUDGE_LOOK_AT): Verdict {
  if (score >= goAt) return "go";
  return score >= lookAt ? "look" : "no";
}

/**
 * How much of a business page `outreach_setup` needs before it will write anything from it.
 *
 * Below this there is nothing to write a configuration from, and a draft invented from a workspace name is worse
 * than no draft: the owner would spend their first hour editing somebody else's guesses instead of writing their
 * own two lines. The route refuses under it.
 */
export const SETUP_PROFILE_MIN = 40;

/** A country code as everything here stores it: two letters, upper case. Null when it is not one. */
export function country(raw: string): string | null {
  const c = raw.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(c) ? c : null;
}

/* ------------------------------------------------------------------------------------- Places -------------- */

/**
 * Where a workspace looks: a country, and optionally one town inside it
 * ([ADR 0043](../../../docs/adr/0043-reach-is-a-list-of-places-and-a-place-is-a-country-or-one-town-inside-it.md)).
 *
 * An **empty town is the country taken whole**, which is what a business selling at a distance reaches and what
 * every row written before this ADR already meant. A plumber in Ledbury has four places in one country; Overbill
 * has three countries and no town between them. One shape serves both, and neither is anybody's default.
 */
export interface Place {
  country: string;
  /** A town inside it, or "" for the whole country. Not a region, a county or a postcode: what a map is asked for. */
  town: string;
}

/** The longest a town may be, which is what a map search takes and a person types. */
export const TOWN_MAX = 80;

/**
 * A town as everything here stores it, or null when it is not one.
 *
 * Deliberately permissive about letters, because the towns this product looks in are spelled in every language its
 * markets use — Ross-on-Wye, Saint-Étienne, 's-Hertogenbosch. What it refuses is the punctuation that would make a
 * `placeKey` ambiguous, and anything long enough to be a sentence rather than a place.
 */
export function town(raw: string): string | null {
  const t = raw.trim().replace(/\s+/g, " ");
  if (!t || t.length > TOWN_MAX) return null;
  return /[/|<>]/.test(t) ? null : t;
}

/** A place, tidied, or null when it is not one. An empty or missing town is the country taken whole. */
export function place(rawCountry: string, rawTown = ""): Place | null {
  const c = country(rawCountry);
  if (!c) return null;
  if (!rawTown.trim()) return { country: c, town: "" };
  const t = town(rawTown);
  return t ? { country: c, town: t } : null;
}

/**
 * A place as one string, for the arrays that hold a list of them: `GB`, or `GB/Ledbury`.
 *
 * Two columns where a place is a dimension the tables index and join on, one string where it is a list inside a
 * row (ADR 0043 point 7). A join table for a group's own places would be a table nobody needed.
 */
export const placeKey = (p: Place): string => (p.town ? `${p.country}/${p.town}` : p.country);

/** `placeKey` read back. Null when the string is not one, so a row written by hand cannot become a search. */
export function parsePlace(key: string): Place | null {
  const cut = key.indexOf("/");
  return cut === -1 ? place(key) : place(key.slice(0, cut), key.slice(cut + 1));
}

/** Whether two places are the same one. */
export const samePlace = (a: Place, b: Place): boolean => a.country === b.country && a.town === b.town;

/**
 * Every country a market can be, as ISO 3166-1 alpha-2.
 *
 * The screen used to ask for two letters typed into a box, which is a quiz: somebody who sells to Germany had to
 * know it is `DE` and not `GE` or `GER`, and a wrong guess was accepted silently because `country()` above takes
 * any two letters. The list is here rather than in the web app because the API validates against the same idea of
 * what a country is, and two lists would eventually disagree.
 *
 * It is the whole set rather than a chosen few: which countries are *main* is a fact about a business, not about
 * this product, and a bakery in Riga is not served by a list that stops at the G7.
 */
export const COUNTRY_CODES: readonly string[] = [
  "AD", "AE", "AF", "AG", "AI", "AL", "AM", "AO", "AQ", "AR", "AS", "AT", "AU", "AW", "AX", "AZ",
  "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI", "BJ", "BL", "BM", "BN", "BO", "BQ", "BR", "BS", "BT", "BV", "BW", "BY", "BZ",
  "CA", "CC", "CD", "CF", "CG", "CH", "CI", "CK", "CL", "CM", "CN", "CO", "CR", "CU", "CV", "CW", "CX", "CY", "CZ",
  "DE", "DJ", "DK", "DM", "DO", "DZ",
  "EC", "EE", "EG", "EH", "ER", "ES", "ET",
  "FI", "FJ", "FK", "FM", "FO", "FR",
  "GA", "GB", "GD", "GE", "GF", "GG", "GH", "GI", "GL", "GM", "GN", "GP", "GQ", "GR", "GS", "GT", "GU", "GW", "GY",
  "HK", "HM", "HN", "HR", "HT", "HU",
  "ID", "IE", "IL", "IM", "IN", "IO", "IQ", "IR", "IS", "IT",
  "JE", "JM", "JO", "JP",
  "KE", "KG", "KH", "KI", "KM", "KN", "KP", "KR", "KW", "KY", "KZ",
  "LA", "LB", "LC", "LI", "LK", "LR", "LS", "LT", "LU", "LV", "LY",
  "MA", "MC", "MD", "ME", "MF", "MG", "MH", "MK", "ML", "MM", "MN", "MO", "MP", "MQ", "MR", "MS", "MT", "MU", "MV", "MW", "MX", "MY", "MZ",
  "NA", "NC", "NE", "NF", "NG", "NI", "NL", "NO", "NP", "NR", "NU", "NZ",
  "OM",
  "PA", "PE", "PF", "PG", "PH", "PK", "PL", "PM", "PN", "PR", "PS", "PT", "PW", "PY",
  "QA",
  "RE", "RO", "RS", "RU", "RW",
  "SA", "SB", "SC", "SD", "SE", "SG", "SH", "SI", "SJ", "SK", "SL", "SM", "SN", "SO", "SR", "SS", "ST", "SV", "SX", "SY", "SZ",
  "TC", "TD", "TF", "TG", "TH", "TJ", "TK", "TL", "TM", "TN", "TO", "TR", "TT", "TV", "TW", "TZ",
  "UA", "UG", "UM", "US", "UY", "UZ",
  "VA", "VC", "VE", "VG", "VI", "VN", "VU",
  "WF", "WS",
  "YE", "YT",
  "ZA", "ZM", "ZW",
];

/**
 * What a country is called, in the reader's own language where the browser can say so.
 *
 * `Intl.DisplayNames` is in every browser this app supports and in Node, so the names are never a table this repo
 * has to keep correct — a country that renames itself is the platform's problem and not a pull request here. When
 * it cannot answer, the code itself is shown, which is what the screen showed before this existed.
 */
export function countryName(code: string, locale = "en"): string {
  const c = country(code);
  if (!c) return code;
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(c) ?? c;
  } catch {
    return c;
  }
}

/** Every country, named and sorted the way a person reads a list: by name, not by code. */
export function countriesByName(locale = "en"): Array<{ code: string; name: string }> {
  return COUNTRY_CODES
    .map((code) => ({ code, name: countryName(code, locale) }))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
}

/**
 * A website domain as every finder must agree to write it: lower case, no scheme, no `www.`, no path.
 *
 * It is the first key a company is known by, so two finders that spell it differently would be two companies, and
 * the second letter would go to somebody who has already had one.
 */
export function cleanProspectDomain(raw: string): string | null {
  let s = raw.trim().toLowerCase();
  if (!s) return null;
  s = s.replace(/^[a-z][a-z0-9+.-]*:\/\//, "").replace(/^www\./, "");
  s = s.split(/[/?#]/)[0] ?? "";
  s = s.replace(/\.$/, "");
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(s)) return null;
  return s;
}

/* ------------------------------------------------------------------------------------------ the judge ------- */

/** One row of a workspace's table, as the judge reads it. */
export interface JudgeRow {
  id: string;
  kind: JudgeRowKind;
  fact: string;
  /** May be negative — an honest "not found" is evidence and should cost something — and may be zero. */
  points: number;
  /** Without this fact a company is at most a `look`, however well it scores (Overbill's `needed_for_go`). */
  requiredForGo?: boolean;
}

/** What was actually read about one company: which rows its pages support, and where each was read. */
/**
 * The id a fact carries when it says what a company *is* rather than answering a row of the table: read by the judge
 * alongside the table's facts, kept on the sheet, handed to the opening line, and scored by nothing.
 */
export const ABOUT_FACT = "about";

export interface FoundFact {
  /** The judge row this answers, one of `GENERIC_REFUSALS`' codes, or `ABOUT_FACT`. */
  id: string;
  says: string;
  url: string;
  /** How it was established: a model reading a page, code checking something, or a person saying so. */
  by: "model" | "code" | "person";
}

/** Everything code establishes about a company without asking anybody: the three refusals this product always makes. */
export interface CodeChecks {
  /** The address we would write to is on a free provider, or there is no address at all on its own domain. */
  freeMailbox: boolean;
  /** The site is parked, dead, or now something else. */
  notTrading: boolean;
  /** The company is one the business's own page names under *Who else they could choose*. */
  aRival: boolean;
  /** Nothing has been read about it yet: not a verdict, a `look` (see below). */
  unread: boolean;
}

export interface Judgement {
  verdict: Verdict;
  score: number;
  /** Every row that scored, in the order it scored, and every refusal that fired. */
  reasons: Array<{ id: string; says: string; points: number; refusal: boolean }>;
}

/**
 * The judge (Overbill's `docs/50` §4, made per workspace).
 *
 * Three rules, and the order matters.
 *
 * 1. **A refusal is a no, whatever the score.** The three in `GENERIC_REFUSALS` are the product's and cannot be
 *    switched off; the rest are the workspace's own rows. A company that scores 95 and writes from a Gmail address
 *    is still a no, because there is nobody to write to.
 * 2. **Not knowing is a `look`, never a `no`.** A company nobody has read beyond its row has not failed anything.
 *    This is the rule that decides whether the tool is honest: a `no` is a decision, and a decision needs evidence.
 * 3. **A fact marked `requiredForGo` and not found holds a company at a `look`**, however well it scores (Overbill's
 *    `needed_for_go`). A company that ticks every other box and shows no sign of shipping anything is not a shipper.
 * 4. **Otherwise the score decides**, against this workspace's own two lines.
 *
 * Pure, so the whole of it can be proved without a database, a model or a website — which is the point of keeping
 * the deciding in code and the reading at the edges.
 */
export function judge(
  rows: readonly JudgeRow[],
  found: readonly FoundFact[],
  checks: CodeChecks,
  lines: { goAt: number; lookAt: number } = { goAt: JUDGE_GO_AT, lookAt: JUDGE_LOOK_AT },
): Judgement {
  const byId = new Map(rows.map((r) => [r.id, r]));
  const seen = new Set<string>();
  const reasons: Judgement["reasons"] = [];
  let score = 0;
  let refused = false;

  for (const fact of found) {
    // A fact answered twice counts once: two pages saying the same thing is one fact about the company.
    if (seen.has(fact.id)) continue;
    const row = byId.get(fact.id);
    if (!row) continue;
    seen.add(fact.id);
    if (row.kind === "refusal") {
      refused = true;
      reasons.push({ id: row.id, says: row.fact, points: 0, refusal: true });
    } else {
      score += row.points;
      reasons.push({ id: row.id, says: row.fact, points: row.points, refusal: false });
    }
  }

  // The three the product always refuses, after the workspace's own, so a screen lists them in a stable order.
  for (const generic of GENERIC_REFUSALS) {
    const hit = generic.code === "free_mailbox" ? checks.freeMailbox
      : generic.code === "not_trading" ? checks.notTrading
        : checks.aRival;
    if (!hit) continue;
    refused = true;
    reasons.push({ id: generic.code, says: generic.says, points: 0, refusal: true });
  }

  if (refused) return { verdict: "no", score, reasons };
  // Nothing read is not a verdict. A company straight off a finder sits at `look` until somebody or something
  // looks at it, whatever its bare row happens to score.
  if (checks.unread) return { verdict: "look", score, reasons };
  // A fact the workspace says a `go` cannot do without. Missing, the most it can be is a look — never a no, because
  // not finding a fact is not the same as establishing its opposite.
  const missingRequired = rows.filter((r) => r.kind === "score" && r.requiredForGo && !seen.has(r.id));
  for (const row of missingRequired) reasons.push({ id: row.id, says: `not found, and a go needs it: ${row.fact}`, points: 0, refusal: false });
  const decided = verdictFor(score, lines.goAt, lines.lookAt);
  if (missingRequired.length && decided === "go") return { verdict: "look", score, reasons };
  return { verdict: decided, score, reasons };
}

/** The highest a workspace's table can score, which is what a screen shows a score out of. */
export function judgeTotal(rows: readonly JudgeRow[]): number {
  return rows.filter((r) => r.kind === "score").reduce((n, r) => n + r.points, 0);
}

/* ------------------------------------------------------------------------------------- Markets ------------- */

/**
 * The countries a business page suggests it sells to, most likely first.
 *
 * Read off *Where to find us* and *Who buys it*, which is where a business says where it is and who buys from it.
 * Deliberately shallow: it is a proposal the owner accepts or replaces in one press, and a clever guess that is
 * wrong costs more than an obvious one. A page that names nowhere gets nothing rather than a default, because
 * assuming a country for somebody is how a tool writes letters into a market they do not sell to.
 */
export function suggestedMarkets(profile: { sections: Array<{ key: string; body: string }> } | null): string[] {
  if (!profile) return [];
  const said = profile.sections
    .filter((s) => s.key === "where" || s.key === "who" || s.key === "name")
    .map((s) => s.body)
    .join("\n")
    .toLowerCase();
  if (!said.trim()) return [];
  const found: string[] = [];
  for (const [code, words] of COUNTRY_WORDS) {
    if (words.some((w) => new RegExp(`(^|[^a-z])${w}([^a-z]|$)`).test(said))) found.push(code);
  }
  return [...new Set(found)].slice(0, 6);
}

/**
 * Words that name a country on a business page, for `suggestedMarkets`.
 *
 * Only the markets this product has letters for, plus the postcode shapes and the town-less giveaways that appear
 * on a small business's own page. It is not a gazetteer and is not meant to become one: everything it misses, the
 * owner types once.
 */
const COUNTRY_WORDS: ReadonlyArray<readonly [string, readonly string[]]> = [
  ["GB", ["uk", "u\\.k\\.", "united kingdom", "england", "scotland", "wales", "britain", "british", "london", "manchester", "birmingham", "glasgow", "edinburgh"]],
  ["IE", ["ireland", "irish", "dublin", "cork", "galway"]],
  ["FR", ["france", "french", "paris", "lyon", "marseille", "bordeaux"]],
  ["ES", ["spain", "spanish", "espa\\u00f1a", "madrid", "barcelona", "valencia"]],
  ["US", ["usa", "u\\.s\\.", "united states", "america", "american", "new york", "texas", "california"]],
  ["DE", ["germany", "german", "deutschland", "berlin", "munich", "hamburg"]],
  ["NL", ["netherlands", "dutch", "holland", "amsterdam", "rotterdam"]],
  ["BE", ["belgium", "belgian", "brussels", "antwerp"]],
  ["AE", ["uae", "emirates", "dubai", "abu dhabi"]],
  ["TN", ["tunisia", "tunisian", "tunis"]],
];

/**
 * Whether a line asks for a click: it carries a web address, written as a link or as a bare domain with a path.
 *
 * A letter whose answers Joogoos cannot see should ask for something it can count, a link to book a call or to try
 * what is offered on the business's own website, rather than for a reply that lands in an inbox nobody here reads
 * (the owner, 2026-10-21). An email address is not a link: writing *reply to jc@example.com* is asking for a reply.
 */
export function asksForALink(text: string): boolean {
  return /(?:https?:\/\/\S+)|(?<![@\w.-])(?:www\.)?[a-z0-9][a-z0-9-]*(?:\.[a-z0-9-]+)*\.[a-z]{2,}(?:\/\S*)?(?![@\w-])/i.test(text);
}

